import { parsePhoneNumberFromString } from 'libphonenumber-js'
import React from 'react'

import { options_from_hashmap } from '../../../helpers/misc'
import { useImmerStore } from '../../../store'
import { Box, Text } from '../../Shared/Base'
import { FormElementGlobalError } from '../../Shared/Form/Element'
import FormField, { FocusableField, useFormState } from '../../Shared/Form/Field'
import {
  FormPhoneInput,
  FormRadioInput,
  FormSelectNativeInput,
  FormTextareaInput,
  FormTextFieldInput
} from '../../Shared/Form/Input'
import { valid_email, valid_phone, valid_required, valid_zip } from '../../Shared/Form/validators'
import { useFetchApi } from '../../Shared/hooks'
import ModalSimple from '../../Shared/ModalSimple'

const options = options_from_hashmap({
  primo: 'Primo-accédant',
  owner: 'Déjà propriétaire',
  invest: 'Cherche un investissement'
})

const options_civility = options_from_hashmap({
  Mme: 'Madame',
  Mr: 'Monsieur'
})

const ContactForm = ({ form_type, onSubmit }) => {
  const [{ db }] = useImmerStore()
  const db_me_email = db.me && db.me.email

  const user_initial_values = React.useMemo(() => {
    return {
      email: db_me_email && !/appleid.com$/.test(db_me_email) ? db_me_email : '',
      name: '',
      phone: '',
      situation: '',
      civility: '',
      firstname: '',
      lastname: '',
      postal_code: ''
    }
  }, [db_me_email])

  const form_config = React.useMemo(() => {
    switch (form_type) {
      case 'form_rent':
        return {
          initial_values: {
            name: user_initial_values.name,
            email: user_initial_values.email,
            phone: user_initial_values.phone,
            message: ''
          },
          validators: {
            name: { required: valid_required },
            phone: { required: valid_required, is_phone: valid_phone('FR') },
            email: { required: valid_required, is_email: valid_email }
          }
        }

      case 'form_buy':
        return {
          initial_values: {
            name: user_initial_values.name,
            email: user_initial_values.email,
            phone: user_initial_values.phone,
            situation: user_initial_values.situation,
            message: ''
          },
          validators: {
            name: { required: valid_required },
            phone: { required: valid_required, is_phone: valid_phone('FR') },
            email: { required: valid_required, is_email: valid_email },
            situation: { required: valid_required }
          }
        }

      case 'form_neuf':
        return {
          initial_values: {
            civility: user_initial_values.civility,
            firstname: user_initial_values.firstname,
            lastname: user_initial_values.lastname,
            postal_code: user_initial_values.postal_code,
            email: user_initial_values.email,
            phone: user_initial_values.phone
          },
          validators: {
            civility: { required: valid_required },
            firstname: { required: valid_required },
            lastname: { required: valid_required },
            postal_code: { required: valid_required, is_zip: valid_zip },
            phone: { required: valid_required, is_phone: valid_phone('FR') },
            email: { required: valid_required, is_email: valid_email }
          }
        }

      default:
        return null
    }
  }, [form_type, user_initial_values])

  const form = useFormState({
    initial_values: form_config.initial_values,
    validators: form_config.validators,
    onSubmitSuccess: React.useCallback(
      values => {
        onSubmit(values)
      },
      [onSubmit]
    )
  })

  return (
    <Box
      as="form"
      mt={30}
      onSubmit={form.onSubmit}
      my={30}
      onChange={form.onChange}
      width={450}
      maxWidth="100%"
    >
      <FormElementGlobalError globalError={form.globalError} />
      {form_type === 'form_neuf' && (
        <FormField
          showRequiredError={form.submittedOnce}
          name="civility"
          value={form.values.civility}
          validators={form.validators.civility}
          placeholder="Civilité"
          label="Civilité"
          errorLabels={{ required: 'Information obligatoire' }}
          options={options_civility}
        >
          {FormRadioInput}
        </FormField>
      )}

      {form_type !== 'form_neuf' && (
        <Box mt={20}>
          <FocusableField
            showRequiredError={form.submittedOnce}
            name="name"
            value={form.values.name}
            validators={form.validators.name}
            placeholder="Nom"
            errorLabels={{ required: 'Information obligatoire' }}
          >
            {FormTextFieldInput}
          </FocusableField>
        </Box>
      )}

      {form_type === 'form_neuf' && (
        <Box mt={20}>
          <FocusableField
            showRequiredError={form.submittedOnce}
            name="firstname"
            value={form.values.firstname}
            validators={form.validators.firstname}
            placeholder="Prénom"
            errorLabels={{ required: 'Information obligatoire' }}
          >
            {FormTextFieldInput}
          </FocusableField>
        </Box>
      )}

      {form_type === 'form_neuf' && (
        <Box mt={20}>
          <FocusableField
            showRequiredError={form.submittedOnce}
            name="lastname"
            value={form.values.lastname}
            validators={form.validators.lastname}
            placeholder="Nom"
            errorLabels={{ required: 'Information obligatoire' }}
          >
            {FormTextFieldInput}
          </FocusableField>
        </Box>
      )}

      {form_type === 'form_neuf' && (
        <Box mt={20}>
          <FocusableField
            showRequiredError={form.submittedOnce}
            name="postal_code"
            value={form.values.postal_code}
            validators={form.validators.postal_code}
            placeholder="Code postal"
            type="number"
            errorLabels={{ required: 'Information obligatoire', is_zip: 'Code postal incorrect' }}
          >
            {FormTextFieldInput}
          </FocusableField>
        </Box>
      )}

      <Box mt={20}>
        <FocusableField
          type="tel"
          showRequiredError={form.submittedOnce}
          name="phone"
          value={form.values.phone}
          validators={form.validators.phone}
          placeholder="Numéro de téléphone"
        >
          {FormPhoneInput}
        </FocusableField>
      </Box>

      <Box mt={20}>
        <FocusableField
          showRequiredError={form.submittedOnce}
          name="email"
          value={form.values.email}
          validators={form.validators.email}
          placeholder="E-mail"
          errorLabels={{ required: 'Information obligatoire', is_email: 'E-mail incorrect' }}
        >
          {FormTextFieldInput}
        </FocusableField>
      </Box>

      {form_type === 'form_buy' && (
        <Box mt={20}>
          <FocusableField
            showRequiredError={form.submittedOnce}
            name="situation"
            value={form.values.situation}
            validators={form.validators.situation}
            noValueLabel="Votre situation"
            options={options}
          >
            {FormSelectNativeInput}
          </FocusableField>
        </Box>
      )}

      {form_type !== 'form_neuf' && (
        <Box mt={20}>
          <FocusableField
            name="message"
            value={form.values.message}
            placeholder="Votre message"
            type="text"
          >
            {FormTextareaInput}
          </FocusableField>
        </Box>
      )}

      <Box
        as="button"
        className="btn is-medium is-n_secondary"
        display="inline-block"
        width={{ sm: 'auto', xs: '100%' }}
        textAlign="center"
        mt={20}
        disabled={form.loading}
      >
        Envoyer
      </Box>

      <Text mt={20} fontSize={14}>
        En confirmant votre demande vous acceptez que vos données soient collectées par Jinka et
        transférées à notre partenaire pour qu'il puisse vous recontacter à propos de cette offre.{' '}
        <Text
          as="a"
          href="https://www.iubenda.com/privacy-policy/31052839/legal"
          target="_blank"
          rel="noopener noreferrer"
          color="n_tertiary"
          className="tdu"
        >
          En savoir plus
        </Text>
        .
      </Text>
    </Box>
  )
}

const ModalContact = ({ onClose, message_form, ad_id }) => {
  const fetcher = useFetchApi()

  const [submitted, set_submitted] = React.useState(false)

  const onSubmit = React.useCallback(
    async values => {
      if (values.phone) {
        values.phone = parsePhoneNumberFromString(values.phone, 'FR').number
      }

      await fetcher(`ad/${ad_id}/contact/${message_form}`, {
        method: 'POST',
        body: JSON.stringify(values)
      })

      set_submitted(true)
    },
    [ad_id, fetcher, message_form]
  )

  return (
    <ModalSimple onClose={onClose}>
      <Box bg="white" flex={1} display="flex" flexDirection="column" alignItems="center">
        {submitted ? (
          <>
            <Text color="tertiary" fontSize={18} mt={30}>
              Votre message a été envoyé
            </Text>

            <Box
              as="button"
              className="btn is-medium is-tertiary"
              display="inline-block"
              width={{ sm: 'auto', xs: '100%' }}
              textAlign="center"
              mt={40}
              onClick={onClose}
            >
              Fermer
            </Box>
          </>
        ) : (
          <ContactForm onSubmit={onSubmit} form_type={message_form} />
        )}
      </Box>
    </ModalSimple>
  )
}
export default ModalContact
